import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import Box from '@mui/material/Box';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import {Typography, alpha} from "@mui/material";

import { useDraggable, useDroppable } from '@dnd-kit/core';

import Bs4FormElement from './Bs4FormElement';
import Bs4MainDispatch from '../Bs4MainDispatch';
import { TYPE_BS4_FORM } from './Bs4Form'
import { getBoxSx, getBoxStyle, useStateUxTemplate, SmallScreenContext } from '../Bs4Theme/Bs4UxTemplates';
import Bs4Icon from '../Bs4Components/Bs4Icon'
import Bs4I18N from '../Bs4I18N';
import { isNullOrNaN, shallowEqual } from '../Bs4Components/Bs4Utils';

export default class Bs4Section extends Bs4FormElement {
  constructor(props) {
    super(props);
  }

  getDefoultUxTemplateInit = (theme) => {
    return {
      header: {            
        color:            theme.palette.primary.contrastText,
        backgroundColor:  theme.palette.primary.light,
      },    
      box: {
        borderRadius:     (2 * theme.shape.borderRadius)+'px',
      },
    };
  }

  getUxTemplate = (theme) => {
    const uxTemplate = this.bs4Form?.uxTemplate?.body?.Form?.Section;
    return deepmerge(this.getDefoultUxTemplateInit(theme), uxTemplate);
  }

  ExpandMore = styled((props) => {
    const { expand, ...other } = props;    
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  ConstructorModeMenu = (props) => {
    const formSection = this;
    const sectionTT = Bs4I18N.getTT('Sekcja', 'Section', this.constructor.name);
    const {activeCurrent} = React.useContext(this.bs4Form.activeCurrentContext);

    const handleEdit = () => {
      const details = {currFormId: formSection.props.section.formId, targetObject: formSection.props.section.formId, targetOption: 298}; 
      Bs4MainDispatch.commandParser('Bs4OpenCard', details, this.bs4KartotekaId);
      props.closeMenu()
    }    

    const handleMenuClose = () => {
      props.closeMenu()
    }

    const handleClickSection = (e) => {
      console.log('...Section...', this.props.section);
      props.closeMenu()
    }

    return (
      <Menu anchorEl={props.anchorEl} open={props.open} onClose={handleMenuClose}>
        <MenuItem>
          <Typography variant='h6' onClick={handleClickSection}>{sectionTT}</Typography>
        </MenuItem>
        {formSection.props.section.formId ?
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><i className="fas fa-cog"></i></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>      
        : null}                
      </Menu>
    )
  }

  ViewConstructorMode = (props) => {
    if (!this.props.bs4ConstructorMode.enabled || this.props.criterion) return null;
    const formSection = this;
    const theme = useTheme();
    const [anchorElMenu, setAnchorElMenu] = React.useState(null);
    const [select, setSelect] = React.useState(false);
    const {activeCurrent} = React.useContext(this.bs4Form.activeCurrentContext);    

    const handleGearMenu = (event) => {
      setAnchorElMenu(event.currentTarget);
    }

    const closeMenu = () => {
      setAnchorElMenu(null);
      setSelect(false);
    }

    const handleSelect = (e) => {
      setSelect(e.type === 'mouseenter' || Boolean(anchorElMenu))
    }

    const showGear = props.showGear || Boolean(anchorElMenu);

    const sxGrill = React.useMemo(() => {
      let gril = this.getGrilleBox(activeCurrent?.formElement === this ? 2 : 1);
      const display = (select || activeCurrent?.formElement === this || (activeCurrent?.formElement?.formElementType === this.formElementType))
      return {
        display: display ? 'block' : 'none',
        ...gril
      }      
    })

    return (
      <React.Fragment>
        <Box sx={{position:'absolute', top:0, right:0, bottom: 0, left: 0, ...sxGrill}}/>
        <IconButton 
          tabIndex={-1}        
          sx={{position:'absolute', top:-5, right:-5, display: showGear ? 'block' : 'none'}} 
          onClick={handleGearMenu}
          onMouseEnter={handleSelect}
          onMouseLeave={handleSelect}
        >
          <i className="fa-regular fa-gear"></i>
        </IconButton>
        <IconButton 
          ref={props.setActivatorNodeRef}
          tabIndex={-1}
          {...props.listeners}
          sx={{position:'absolute', top:-5, left:'50%', display: showGear ? 'block' : 'none', touchAction: 'none'}}
          onMouseEnter={handleSelect}
          onMouseLeave={handleSelect}
        >
          <i className="fa-regular fa-up-down-left-right"></i>
        </IconButton>
        <formSection.ConstructorModeMenu open={Boolean(anchorElMenu)} anchorEl={anchorElMenu} closeMenu={closeMenu}/>
      </React.Fragment>
    )
  }

  ViewFormFields = (props) => {
    const section = this;
    const theme = useTheme();
    const [boxSx, alignItems] = React.useMemo(() => {
      const boxSx = getBoxSx(props.uxTemplate?.box, theme);
      const alignItems = props.uxTemplate?.alignItems;
      return [boxSx, alignItems];
    }, [props.uxTemplate])      
    const width = React.useMemo(() => {
      let width = parseFloat(section.props?.section?.width);
      if (isNullOrNaN(width)) width = 100;
      if (width === 0) width = 'auto';
      else width = String(width + '%');
      return width;      
    }, [section.props?.section?.width]);

    const justifyContent = React.useMemo(() => {
      switch (section.props?.section?.justifyContent || 'left') {
        case 'center': return 'center'
        case 'right': return 'flex-end'
        case 'left':
        default: return 'flex-start'
      }
    }, [section.props?.section?.justifyContent]);
    
    return (
      <Box        
        id={section.getElementId('Collapse')}
        sx={{
          height:'100%', 
          display:"flex", 
          flexDirection:"row", 
          flexWrap:'wrap', 
          padding: section.props.section.frame ? boxSx.padding : 0,
          alignItems: alignItems,
          justifyContent: justifyContent,
        }}
      >
        {section.formElementArray.map((field) => {
          // console.log('...Section fields...', field?.inputId, field)
          return (
            <React.Fragment key={field.inputId}>
              {field.newLine ? <div style={{'flexBasis': '100%', width: '0px', height: '0px', overflow: 'hidden'}}/> : null}     
              <field.ViewComponent showTooltip={props.showTooltip}/>
            </React.Fragment>
          )
        })}
      </Box>
    )
  }  

  formElementType = 2;
  sectionProps = null;  

  ViewComponent = (props) => {      
    const formSection = this;        

    const { setNodeRef: setNodeRefDroppable } = useDroppable({
      id: this.getElementId('Droppable'),
      disabled: !this.props.bs4ConstructorMode.enabled,
      data: {
        formElement: this,
        type: 'form',
      }
    });      

    const { listeners, setNodeRef: setNodeRefDraggable, setActivatorNodeRef} = useDraggable({
      id: this.getElementId('Draggable'),
      disabled: !this.props.bs4ConstructorMode.enabled,
      data: {
        formElement: this,
        type: 'form',
      },
    });

    return (
      <formSection.ViewComponentDne 
        {...props} 
        setNodeRefDroppable={setNodeRefDroppable} 
        setNodeRefDraggable={setNodeRefDraggable}
        setActivatorNodeRef={setActivatorNodeRef} 
        listeners={listeners} 
      />
    )
  }

  ViewComponentDne = (props) => {      
    const formSection = this;
    // formSection.props.section.frame = true;
    // formSection.props.section.collapsible = true;
    // formSection.props.section.collapsed = true;

    this.sectionProps = props;
    const smallScreen = React.useContext(SmallScreenContext);
    const [expanded, setExpanded] = React.useState(!this.props?.section?.collapsed);
    const [display, setDisplay] = React.useState('block')
    const theme = useTheme();
    const refFocusInOut = React.useRef(null);

    const [showGear, setShowGear] = React.useState(false);

    const {dateTimeStamp} = useStateUxTemplate(TYPE_BS4_FORM);
    const uxTemplate = React.useMemo(() => this.getUxTemplate(theme), [dateTimeStamp, theme]);    

    const boxSxHeader = React.useMemo(() => {
      const boxSxHeader = {
        ...getBoxSx(uxTemplate?.header, theme),
        //...getBoxStyle(this.props.section.bs4classcss, theme),
        ...getBoxSx(this.props.section.bs4classcss, theme),
      }
      if (!boxSxHeader.color) boxSxHeader.color = theme.palette.primary.contrastText;
      if (!boxSxHeader.backgroundColor) boxSxHeader.backgroundColor = theme.palette.primary.light;            
      return boxSxHeader;
    }, [uxTemplate]);
    const boxSx = React.useMemo(() =>  {
      const boxSx = getBoxSx(uxTemplate?.box, theme);
      if (!this.props.section.frame) {
        boxSx.backgroundColor = 'transparent';
        boxSx.borderStyle = 'none';
        boxSx.margin = 0;
        boxSx.boxShadow = 'none';
        boxSx.borderRadius = 0;
      }
      return boxSx;
    }, [uxTemplate]);

    const width = React.useMemo(() => {
      let width = parseFloat(formSection.props?.section?.width);
      if (isNullOrNaN(width)) width = 100;
      if (width === 0) width = 'auto';
      else width = String(width + '%');
      return width;      
    }, [formSection.props?.section?.width]);

    const fontSize = theme.typography?.[boxSxHeader.typography]?.fontSize;

    const handleExpandClick = React.useCallback(() => {      
      const expand = !(expanded && this.props.section.collapsible);      
      if (!expand && props.showTooltip && formSection.formElementArray.find(field => field.isEqualId(props.showTooltip?.id))) {        
        props.showTooltip.closeTooltip();
        props.showTooltip.setShowTooltip(null);
      }
      setExpanded(expand);
    }, [expanded, setExpanded, props.showTooltip, props.showTooltip?.id]);
    
    this.removeElementDom = () => {
      if (this.props.section.collapsible) setExpanded(false)
      else setDisplay('none')
    }

    this.showElementDom = () => {
      if (this.props.section.collapsible) setExpanded(true)
      else setDisplay('block')
    }

    const handleShowGear = React.useCallback((e) => {
      if (!this.props.bs4ConstructorMode.enabled) return null;
      setShowGear(e.type === 'mouseenter');
    }, [setShowGear]);

    const spacing = props.uxKartoteka?.spacing || 0;
    const propsFlexs = this.props.section?.bs4flexframe ? {flex:1} : {} 
    const paddings = smallScreen ? {
      pt: spacing,
      pr: spacing,
      pb: spacing,
      pl: spacing,
    } : {
      pt: props.sectionStart    ? spacing : spacing / 2,
      pr: props.partitionEnd    ? spacing : spacing / 2,
      pb: props.sectionEnd      ? spacing : spacing / 2,
      pl: props.partitionStart  ? spacing : spacing / 2,
    };    

    React.useEffect(() => {
      if (props.showTooltip && formSection.formElementArray.find(field => field.isEqualId(props.showTooltip.id))) {        
        setExpanded(true);
      }
    }, [props.showTooltip, setExpanded])

    React.useEffect(() => {
      this.getFormElementSize = () => {
        if (props.size) return props.size;
        if (refFocusInOut.current) {
          const rect = refFocusInOut.current.getBoundingClientRect();
          return {
            width: rect.width,
            height: rect.height,
          }
        }
      }
    }, [refFocusInOut]);

    const refSpeedTest = React.useRef({
      shallowEqualValues: null,
      shallowEqualProps: null,
    });    

    Bs4MainDispatch.speedRun((tag) => {
      const newObj = {  
        expanded,
        display,        
        showGear
      };
      console.log(Bs4MainDispatch.speedNextTime(tag), Bs4MainDispatch.speedBs4KarOpt(this.bs4KartotekaId, this.optionId), shallowEqual(newObj, refSpeedTest.current.shallowEqualValues, Bs4MainDispatch.speedPrefix+tag+' EqualValues'), shallowEqual(props, refSpeedTest.current.shallowEqualProps, Bs4MainDispatch.speedPrefix+tag+' EqualProps'), props);
      refSpeedTest.current.shallowEqualValues = newObj;
      refSpeedTest.current.shallowEqualProps = props;
    }, '..Section..');

    return (
      <Box        
        ref={node => {
          refFocusInOut.current = node;
          props.setNodeRefDraggable(node);
          props.setNodeRefDroppable(node)
        }}
        id={formSection.getElementId()}
        position={'relative'}
        {...propsFlexs}
        overflow='hidden'
        display={display} 
        {...paddings}
        width={width}
        onMouseEnter={handleShowGear}
        onMouseLeave={handleShowGear}        
      >
        <Card
          id={formSection.getElementId('Card')}
          sx={{
            ...boxSx,
            margin: 0,
            padding: 0,
            height:'100%',
          }}
        >
          {formSection.props.section.frame
          ?
            <React.Fragment>
              <CardHeader
                id={formSection.getElementId('CardHeader')}
                onClick={handleExpandClick}
                sx={boxSxHeader}
                avatar={<Bs4Icon iconCls={formSection.props.section?.iconCls} iconStyle={formSection.props.section?.iconStyle} instead={<span></span>}/>}
                action={
                  <formSection.ExpandMore
                    expand={expanded}
                    onClick={handleExpandClick}
                    sx={{
                      color: boxSxHeader.color,
                      visibility: formSection.props.section.collapsible ? 'inherit' : 'hidden'
                    }}
                  >
                    <ExpandMoreIcon sx={{color: boxSxHeader.color, fontSize: fontSize}}/>
                  </formSection.ExpandMore>
                }
                title={<Typography variant={boxSxHeader.typography || ''}>{formSection.props.section.title}</Typography>}
              />
              <Collapse in={expanded}>
                <formSection.ViewFormFields uxTemplate={uxTemplate} showTooltip={props.showTooltip}/>
              </Collapse>
            </React.Fragment>
          :
            <formSection.ViewFormFields uxTemplate={uxTemplate} showTooltip={props.showTooltip}/>
          }                     
        </Card>
        <formSection.ViewConstructorMode 
          showGear={showGear} 
          setActivatorNodeRef={props.setActivatorNodeRef}
          listeners={props.listeners}
        />
      </Box>
    );
  }
}